import type { PillTone } from '@/components/primitives/StatusPill';

/**
 * Driver-facing OCPP status surface for connector status badges.
 *
 * Moved verbatim from StationDetailPage (its connector-row badge) so the
 * My Home charger card renders the SAME status set and color code as the
 * public side — one canonical mapping, no forks.
 *
 * Maps the nine OCPP status values (defined in
 * `packages/shared/src/types/charger.ts:11`) plus the synthetic
 * `Offline` / `Unknown` cases to {label, tone, selectable} triples.
 * Drivers picking a stall care about three things this lookup
 * encodes:
 *   - LABEL: human copy ("Charging", "Paused", "Wrapping up", etc.)
 *   - TONE: StatusPill tone for color signal (good/info/power/warn/
 *           danger/mute/accent — see StatusPill.tsx PillTone)
 *   - SELECTABLE: whether the row should be tappable. Available
 *     (canonical free state) AND Preparing (cable plugged in,
 *     waiting for app authorization — the plug-FIRST flow per
 *     OCPP 1.6) both allow starting a new session. All other
 *     states render the badge but disable the radio.
 *
 * SuspendedEV vs SuspendedEVSE: the OCPP distinction (vehicle-
 * initiated vs station-initiated pause) doesn't help a driver
 * deciding which stall to pick — both mean "session active, no
 * current flowing." Collapsed to "Paused" with the same warn tone.
 * Admin tools surface the technical distinction when ops need it.
 *
 * Reserved → `accent` tone (themed accent, distinct from the
 * blue/green/yellow signals used by the other in-use states).
 * The palette has no `purple` token, and accent draws the right
 * "noteworthy" attention without competing with the green
 * Available baseline.
 *
 * Finishing vs Paused: both are "session active, no draw," but
 * Finishing reads as "soon-free" (positive, BMS completed) while
 * Paused reads as "stuck" (negative, may not free up). Mapped to
 * different tones — power (yellow) for Finishing, warn (red) for
 * Paused — so drivers can scan for the soon-free signal at a
 * glance without checking labels.
 */
export type ConnectorStatusKey =
  | 'offline'
  | 'available'
  | 'charging'
  | 'pluggedIn'
  | 'finishing'
  | 'paused'
  | 'reserved'
  | 'faulted'
  | 'unavailable'
  | 'unknown';

export type ConnectorStatusView = {
  /** Locale-independent id of the bucket. `label` is the English copy
   *  the older cards still print raw; surfaces that localize the pill
   *  translate `connectorStatus.<key>` in their own namespace and fall
   *  back to `label`. Additive — every existing consumer keeps reading
   *  label/tone/selectable unchanged. */
  key: ConnectorStatusKey;
  label: string;
  tone: PillTone;
  selectable: boolean;
};

export function viewForConnectorStatus(
  status: string | undefined,
  isOnline: boolean,
): ConnectorStatusView {
  // Charger-level offline check wins — the OCPP status field is
  // stale once the WebSocket has dropped. `is_online` is the live
  // connectivity signal maintained by the OCPP server's heartbeat
  // tracking. Drivers shouldn't try to start sessions on a charger
  // that can't receive the StartTransaction command, regardless of
  // what the last-known connector status was.
  if (!isOnline) {
    return { key: 'offline', label: 'Offline', tone: 'mute', selectable: false };
  }
  switch (status) {
    case 'Available':
      return { key: 'available', label: 'Available', tone: 'good', selectable: true };
    case 'Charging':
      return { key: 'charging', label: 'Charging', tone: 'info', selectable: false };
    case 'Preparing':
      // OCPP 1.6 Preparing = "cable detected + plugged in, waiting
      // for session authorization." This is the canonical
      // plug-FIRST flow: driver inserts the cable before opening
      // the app. Charger reports Preparing; the app needs to issue
      // RemoteStartTransaction to authorize and the session begins.
      //
      // Previously mapped to {info, selectable:false} on the
      // mistaken assumption that Preparing meant "session in
      // progress." That gate blocked the start flow entirely from
      // the plug-first path — driver couldn't tap the row to
      // select the connector, the Start CTA never enabled.
      //
      // Now mapped to {good, selectable:true} so the driver can
      // select the connector + start the session normally. Label
      // changed to "Plugged in" so the affordance reads clearly
      // (the technical "Preparing" enum is operator-facing; drivers
      // care that the cable is detected and the row is actionable).
      // Per OCPP 1.6 the charger accepts RemoteStartTransaction in
      // both Available AND Preparing states; the OCPP server-side
      // dispatch (apps/api/src/routes/chargers/commands.ts) is
      // generic and doesn't pre-gate on status, so this frontend
      // unblock is the entire fix.
      return { key: 'pluggedIn', label: 'Plugged in', tone: 'good', selectable: true };
    case 'Finishing':
      // BMS-stop or near-full, plug still attached. Soon-free.
      return { key: 'finishing', label: 'Finishing', tone: 'power', selectable: false };
    case 'SuspendedEV':
    case 'SuspendedEVSE':
      // Mid-session pause. EV-initiated (vehicle BMS) vs
      // EVSE-initiated (charger load management) collapses to
      // "Paused" for drivers — they're picking a stall, not
      // diagnosing the pause cause.
      return { key: 'paused', label: 'Paused', tone: 'warn', selectable: false };
    case 'Reserved':
      return { key: 'reserved', label: 'Reserved', tone: 'accent', selectable: false };
    case 'Faulted':
      // Hardware fault. Distinct from Paused (which is a normal
      // mid-session pause) — same tone visually because the
      // palette doesn't separate red-orange from red, but the
      // label disambiguates.
      return { key: 'faulted', label: 'Faulted', tone: 'danger', selectable: false };
    case 'Unavailable':
      // Operator-disabled. Same mute as Offline — both communicate
      // "not actionable for you right now" without the urgency
      // signal of Faulted.
      return { key: 'unavailable', label: 'Unavailable', tone: 'mute', selectable: false };
    default:
      // Defensive fallback for any future OCPP status value not
      // yet mapped here, OR malformed data. Renders quietly rather
      // than crashing the row.
      return { key: 'unknown', label: 'Unknown', tone: 'mute', selectable: false };
  }
}
